/**
 * A4 Invoice PDF download — uses company Receipt Template settings where applicable,
 * with proper multi-page breaks and compressed JPEG pages for smaller downloads.
 */
import { formatMoney } from '@/utils/currency';
import {
  parseReceiptTemplateSettings,
} from '@/utils/receiptTemplate';
import type { ReceiptTemplateSettings } from '@/types';
import { isUk, ukVatRateCode } from '@/data/locales';

export type InvoicePdfSale = {
  name: string;
  invoice_date?: string;
  creation?: string;
  payment_method?: string;
  notes?: string;
  owner?: string;
  cashier?: string;
  cashier_name?: string;
  status?: string;
  customer?: {
    customer_name?: string;
    name?: string;
    mobile?: string;
    email?: string;
    address?: string;
    city?: string;
    postal_code?: string;
  };
  items: Array<{
    item_code?: string;
    item_name?: string;
    line_comment?: string;
    quantity: number;
    rate?: number;
    amount?: number;
    variant_name?: string;
    /** POS Item tax_rate percent (0, 5, 20, …) */
    tax_rate?: number;
    original_rate?: number;
    promo_amount?: number;
  }>;
  payments?: Array<{ payment_method?: string; amount?: number; reference_no?: string }>;
  financials?: {
    subtotal?: number;
    /** Pre-promotion goods total (subtotal + promo_total) */
    subtotal_original?: number;
    vat?: number;
    tax?: number;
    discount_amount?: number;
    promo_total?: number;
    write_off_amount?: number;
    prices_include_tax?: boolean;
    total?: number;
    paid_amount?: number;
  };
};

export type InvoicePdfOptions = {
  companyName?: string;
  companyAddress?: string;
  companyPhone?: string;
  companyEmail?: string;
  companyLogo?: string;
  receiptTemplate?: ReceiptTemplateSettings | string | null;
  /** Company country — VAT Rate column only applies for UK */
  country?: string | null;
};

const PAGE_CSS_WIDTH = 794;
const ROWS_PER_PAGE = 20;

function escapeHtml(s: unknown) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function sanitize(value: unknown): string {
  if (value == null) return '';
  const s = String(value).trim();
  if (!s) return '';
  if (
    /\b(const|let|var|function|import|export|return)\b/.test(s) ||
    /[{};=<>]/.test(s) ||
    /\?.*:/.test(s) ||
    /subtotalDisplay|promoTotals|showPromoRow|frappe\./i.test(s)
  ) {
    return '';
  }
  return s;
}

function formatUkDate(value?: string) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return escapeHtml(String(value).split(' ')[0]);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function chunkItems<T>(items: T[], size: number): T[][] {
  if (!items.length) return [[]];
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

async function optimizeLogo(src?: string): Promise<string> {
  if (!src) return '';
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.crossOrigin = 'anonymous';
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = src;
    });
    const maxW = 160;
    const maxH = 56;
    const scale = Math.min(1, maxW / img.width, maxH / img.height);
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return src;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL('image/jpeg', 0.92);
  } catch {
    return src;
  }
}

function fontSizePx(tmpl: ReceiptTemplateSettings): number {
  if (tmpl.font_size === 'small') return 10;
  if (tmpl.font_size === 'large') return 12.5;
  return 11;
}

function buildInvoicePage(
  sale: InvoicePdfSale,
  opts: InvoicePdfOptions & { logoSrc?: string; tmpl: ReceiptTemplateSettings },
  pageItems: InvoicePdfSale['items'],
  itemOffset: number,
  pageIndex: number,
  pageCount: number,
  isLastPage: boolean,
) {
  const tmpl = opts.tmpl;
  const company = sanitize(opts.companyName) || 'Company';
  const address = tmpl.show_address === false ? '' : sanitize(opts.companyAddress);
  const phone = tmpl.show_phone === false ? '' : sanitize(opts.companyPhone);
  const email = tmpl.show_email === false ? '' : sanitize(opts.companyEmail);
  const showLogo = tmpl.show_logo !== false && opts.logoSrc;
  const logo = showLogo
    ? `<img src="${escapeHtml(opts.logoSrc)}" alt="" style="max-height:${Math.min(tmpl.logo_width || 48, 56)}px;max-width:140px;object-fit:contain;margin-bottom:4px;" />`
    : '';
  const headerExtra = sanitize(tmpl.header_text);
  const footerExtra = sanitize(tmpl.footer_text);
  const fs = fontSizePx(tmpl);

  const c = sale.customer || {};
  const customerName = sanitize(c.customer_name || c.name) || 'Walk-in Customer';
  const customerContact = [sanitize(c.mobile), sanitize(c.email), sanitize([c.address, c.city, c.postal_code].filter(Boolean).join(', '))]
    .filter(Boolean)
    .join(' · ');
  const cashier = sanitize(sale.cashier_name || sale.cashier || sale.owner) || '—';

  const fin = sale.financials || {};
  const chargedSubtotal = Number(fin.subtotal) || 0;
  const promo = Number(fin.promo_total) || 0;
  let discount = Number(fin.discount_amount) || 0;
  const writeOff = Number(fin.write_off_amount) || 0;
  const tax = Number(fin.vat ?? fin.tax) || 0;
  const pricesIncl = Boolean(fin.prices_include_tax);
  // Drop phantom discount that mirrors included VAT (legacy sales-hub bug)
  if (pricesIncl && discount > 0 && tax > 0 && Math.abs(discount - tax) < 0.02) {
    discount = 0;
  }
  const displaySubtotal =
    promo > 0
      ? Number(fin.subtotal_original) || chargedSubtotal + promo
      : chargedSubtotal;
  // Prefer stored total. Tax-inclusive: VAT is extracted from gross (not additive).
  // Tax-exclusive fallback: subtotal − discount − write-off + VAT.
  const total =
    fin.total != null && Number.isFinite(Number(fin.total))
      ? Number(fin.total)
      : pricesIncl
        ? Math.max(0, chargedSubtotal - discount - writeOff)
        : Math.max(0, chargedSubtotal - discount - writeOff + tax);
  const paid =
    fin.paid_amount != null
      ? Number(fin.paid_amount)
      : (sale.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const balance = Math.max(0, total - paid);
  const showPrices = tmpl.show_item_price !== false;
  const showTotals = tmpl.show_totals !== false;
  const showItemCode = tmpl.show_item_code !== false;
  const showCustomer = tmpl.show_customer !== false;
  const showServedBy = tmpl.show_served_by !== false;
  const showVatRate = Boolean(tmpl.show_vat_rate) && isUk(opts.country);

  const rows = pageItems
    .map((it, idx) => {
      const qty = Number(it.quantity) || 0;
      const rate = Number(it.rate) || 0;
      const amount = Number(it.amount) || qty * rate;
      const originalRate = Number(it.original_rate) || 0;
      const promoAmount = Number(it.promo_amount) || 0;
      const hasPromo = promoAmount > 0.004 && originalRate > rate + 0.004;
      const name = it.item_name || it.item_code || 'Item';
      const variant = it.variant_name ? ` — ${it.variant_name}` : '';
      const lineNo = itemOffset + idx + 1;
      const vatCode = ukVatRateCode(it.tax_rate);
      const rateCell = hasPromo
        ? `<div style="text-decoration:line-through;color:#888;font-size:${fs - 1.5}px;">${escapeHtml(formatMoney(originalRate))}</div><div>${escapeHtml(formatMoney(rate))}</div>`
        : escapeHtml(formatMoney(rate));
      const amountCell = hasPromo
        ? `<div style="text-decoration:line-through;color:#888;font-size:${fs - 1.5}px;font-weight:500;">${escapeHtml(formatMoney(amount + promoAmount))}</div><div>${escapeHtml(formatMoney(amount))}</div>`
        : escapeHtml(formatMoney(amount));
      return `
        <tr>
          <td style="padding:6px 4px;border-bottom:1px solid #ccc;font-size:${fs}px;">
            <div style="font-weight:600;"><span style="color:#888;font-weight:500;margin-right:4px;">${lineNo}.</span>${escapeHtml(name)}${escapeHtml(variant)}</div>
            ${
              showItemCode && it.item_code && it.item_code !== it.item_name
                ? `<div style="font-size:${fs - 1.5}px;color:#555;font-family:monospace;">${escapeHtml(it.item_code)}</div>`
                : ''
            }
            ${
              it.line_comment
                ? `<div style="font-size:${fs - 1}px;color:#444;font-style:italic;margin-top:2px;">${escapeHtml(it.line_comment)}</div>`
                : ''
            }
          </td>
          <td style="padding:6px 4px;text-align:center;border-bottom:1px solid #ccc;font-size:${fs}px;">${escapeHtml(qty)}</td>
          ${
            showPrices
              ? `
          <td style="padding:6px 4px;text-align:right;border-bottom:1px solid #ccc;font-size:${fs}px;">${rateCell}</td>
          <td style="padding:6px 4px;text-align:right;border-bottom:1px solid #ccc;font-size:${fs}px;font-weight:700;">${amountCell}</td>`
              : ''
          }
          ${
            showVatRate
              ? `<td style="padding:6px 4px;text-align:right;border-bottom:1px solid #ccc;font-size:${fs}px;font-weight:700;">${escapeHtml(vatCode)}</td>`
              : ''
          }
        </tr>`;
    })
    .join('');

  const colCount = (showPrices ? 4 : 2) + (showVatRate ? 1 : 0);

  const totalsBlock =
    isLastPage && showTotals
      ? `
        <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-top:12px;gap:16px;">
          <div style="width:48%;font-size:${fs - 0.5}px;">
            ${(sale.payments || []).length
              ? `
              <div style="font-weight:700;margin-bottom:4px;text-transform:uppercase;font-size:9px;letter-spacing:0.04em;">Payment</div>
              ${(sale.payments || [])
                .map(
                  (p) => `
                <div style="display:flex;justify-content:space-between;padding:3px 0;border-bottom:1px solid #ccc;">
                  <span>${escapeHtml(p.payment_method || sale.payment_method || 'Cash')}${
                    p.reference_no ? ` (${escapeHtml(p.reference_no)})` : ''
                  }</span>
                  <span style="font-weight:600;">${escapeHtml(formatMoney(Number(p.amount) || 0))}</span>
                </div>`,
                )
                .join('')}`
              : sale.payment_method
                ? `<div style="font-size:${fs - 0.5}px;"><strong>Payment:</strong> ${escapeHtml(sale.payment_method)}</div>`
                : ''}
          </div>
          <table style="width:240px;font-size:${fs}px;border-collapse:collapse;">
            <tr>
              <td style="padding:3px 0;">Subtotal</td>
              <td style="padding:3px 0;text-align:right;font-weight:600;">${escapeHtml(formatMoney(displaySubtotal))}</td>
            </tr>
            ${
              promo > 0
                ? `<tr><td style="padding:3px 0;">Promotions</td><td style="padding:3px 0;text-align:right;font-weight:600;">− ${escapeHtml(formatMoney(promo))}</td></tr>`
                : ''
            }
            ${
              discount > 0
                ? `<tr><td style="padding:3px 0;">Discount</td><td style="padding:3px 0;text-align:right;font-weight:600;">− ${escapeHtml(formatMoney(discount))}</td></tr>`
                : ''
            }
            ${
              writeOff > 0
                ? `<tr><td style="padding:3px 0;">Write off</td><td style="padding:3px 0;text-align:right;font-weight:600;">− ${escapeHtml(formatMoney(writeOff))}</td></tr>`
                : ''
            }
            ${
              tax > 0
                ? `<tr><td style="padding:3px 0;">${pricesIncl ? 'VAT (included)' : 'VAT'}</td><td style="padding:3px 0;text-align:right;font-weight:600;">${escapeHtml(formatMoney(tax))}</td></tr>`
                : ''
            }
            <tr style="border-top:1px solid #111;border-bottom:2px solid #111;">
              <td style="padding:6px 0;font-weight:700;">Total</td>
              <td style="padding:6px 0;text-align:right;font-weight:700;">${escapeHtml(formatMoney(total))}</td>
            </tr>
            <tr>
              <td style="padding:3px 0;">Amount paid</td>
              <td style="padding:3px 0;text-align:right;font-weight:600;">${escapeHtml(formatMoney(paid || total))}</td>
            </tr>
            ${
              balance > 0
                ? `<tr><td style="padding:3px 0;font-weight:700;">Balance due</td><td style="padding:3px 0;text-align:right;font-weight:700;">${escapeHtml(formatMoney(balance))}</td></tr>`
                : ''
            }
            ${
              pricesIncl && tax > 0
                ? `<tr><td colspan="2" style="padding:6px 0 0;font-size:9px;color:#555;">Prices include tax</td></tr>`
                : ''
            }
          </table>
        </div>`
      : '';

  return `
    <section class="invoice-page" data-order="${escapeHtml(sale.name)}" style="width:${PAGE_CSS_WIDTH}px;box-sizing:border-box;padding:24px 30px 18px;background:#fff;color:#111;font-family:Arial,Helvetica,sans-serif;display:flex;flex-direction:column;min-height:1080px;">
      <div style="flex:1 1 auto;">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:10px;padding-bottom:8px;border-bottom:1px solid #111;">
          <div style="width:55%;">
            ${logo}
            <div style="font-size:17px;font-weight:700;font-family:'Times New Roman',Times,serif;">${escapeHtml(company)}</div>
            <div style="font-size:10px;line-height:1.35;margin-top:2px;color:#333;">
              ${address ? `${escapeHtml(address)}<br>` : ''}
              ${phone ? `Tel: ${escapeHtml(phone)}` : ''}${phone && email ? ' · ' : ''}${email ? `Email: ${escapeHtml(email)}` : ''}
            </div>
            ${headerExtra ? `<div style="margin-top:4px;font-size:10px;color:#333;">${escapeHtml(headerExtra)}</div>` : ''}
          </div>
          <div style="width:42%;text-align:right;">
            <div style="font-size:15px;font-weight:700;letter-spacing:0.06em;font-family:'Times New Roman',Times,serif;">TAX INVOICE</div>
            <div style="margin-top:6px;font-size:11px;line-height:1.4;">
              <div>Invoice No: <strong>${escapeHtml(sale.name)}</strong></div>
              <div>Date: <strong>${formatUkDate(sale.invoice_date || sale.creation)}</strong></div>
              ${showServedBy ? `<div>Served by: <strong>${escapeHtml(cashier)}</strong></div>` : ''}
            </div>
          </div>
        </div>

        ${
          showCustomer
            ? `
        <div style="margin-bottom:10px;padding-bottom:8px;border-bottom:1px solid #ccc;">
          <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;">Billed To</div>
          <div style="font-size:13px;font-weight:700;">${escapeHtml(customerName)}</div>
          ${customerContact ? `<div style="font-size:10px;color:#333;margin-top:2px;">${escapeHtml(customerContact)}</div>` : ''}
        </div>`
            : ''
        }

        <table style="width:100%;border-collapse:collapse;">
          <thead>
            <tr>
              <th style="text-align:left;padding:6px 4px;font-size:9px;font-weight:700;text-transform:uppercase;border-top:1px solid #111;border-bottom:1px solid #111;">Item description</th>
              <th style="text-align:center;padding:6px 4px;font-size:9px;font-weight:700;text-transform:uppercase;border-top:1px solid #111;border-bottom:1px solid #111;width:12%;">Qty</th>
              ${
                showPrices
                  ? `
              <th style="text-align:right;padding:6px 4px;font-size:9px;font-weight:700;text-transform:uppercase;border-top:1px solid #111;border-bottom:1px solid #111;width:18%;">Unit price</th>
              <th style="text-align:right;padding:6px 4px;font-size:9px;font-weight:700;text-transform:uppercase;border-top:1px solid #111;border-bottom:1px solid #111;width:20%;">Amount</th>`
                  : ''
              }
              ${
                showVatRate
                  ? `<th style="text-align:right;padding:6px 4px;font-size:9px;font-weight:700;text-transform:uppercase;border-top:1px solid #111;border-bottom:1px solid #111;width:10%;">VAT Rate</th>`
                  : ''
              }
            </tr>
          </thead>
          <tbody>
            ${
              rows ||
              `<tr><td colspan="${colCount}" style="padding:16px;text-align:center;">No items</td></tr>`
            }
          </tbody>
        </table>

        ${totalsBlock}
      </div>

      <div style="margin-top:14px;padding-top:8px;border-top:1px solid #111;text-align:center;font-size:9px;color:#555;flex-shrink:0;">
        ${footerExtra ? `<div style="margin-bottom:4px;color:#333;">${escapeHtml(footerExtra)}</div>` : ''}
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <span>Thank you for your business · Powered by SaleDesk POS</span>
          <span style="font-weight:700;color:#111;">Page ${pageIndex + 1} of ${pageCount}</span>
        </div>
      </div>
    </section>
  `;
}

export async function generateInvoicePdfHtml(
  sales: InvoicePdfSale[],
  opts: InvoicePdfOptions,
): Promise<string> {
  const tmpl = parseReceiptTemplateSettings(opts.receiptTemplate);
  const logoSrc = await optimizeLogo(opts.companyLogo);
  const pages: string[] = [];

  for (const sale of sales) {
    const items = sale.items || [];
    const chunks = chunkItems(items, ROWS_PER_PAGE);
    const pageCount = chunks.length;
    chunks.forEach((chunk, pageIdx) => {
      pages.push(
        buildInvoicePage(
          sale,
          { ...opts, logoSrc, tmpl },
          chunk,
          pageIdx * ROWS_PER_PAGE,
          pageIdx,
          pageCount,
          pageIdx === pageCount - 1,
        ),
      );
    });
  }

  return `<div id="invoice-pdf-root">${pages.join('')}</div>`;
}

async function generateInvoicePdfDoc(sales: InvoicePdfSale[], opts: InvoicePdfOptions) {
  if (!sales.length) throw new Error('No sales selected');

  const [html2canvas, { jsPDF }] = await Promise.all([
    import('html2canvas').then((m) => m.default),
    import('jspdf'),
  ]);

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-10000px';
  container.style.top = '0';
  container.style.zIndex = '-1';
  container.innerHTML = await generateInvoicePdfHtml(sales, opts);
  document.body.appendChild(container);

  try {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const pages = Array.from(container.querySelectorAll('.invoice-page')) as HTMLElement[];
    let firstJpeg = '';

    for (let i = 0; i < pages.length; i++) {
      const canvas = await html2canvas(pages[i], {
        scale: 2.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: PAGE_CSS_WIDTH,
        windowWidth: PAGE_CSS_WIDTH,
        logging: false,
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      if (i === 0) firstJpeg = imgData;
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      if (i > 0) pdf.addPage();
      const drawH = Math.min(imgHeight, pageHeight);
      const drawW = imgWidth * (drawH / imgHeight);
      const x = (pageWidth - drawW) / 2;
      pdf.addImage(imgData, 'JPEG', x, 0, drawW, drawH, undefined, 'MEDIUM');
    }

    (pdf as any)._imgData = firstJpeg;
    return pdf;
  } finally {
    document.body.removeChild(container);
  }
}

export async function downloadInvoicePdf(sales: InvoicePdfSale[], opts: InvoicePdfOptions) {
  const pdf = await generateInvoicePdfDoc(sales, opts);
  const stamp = new Date().toISOString().slice(0, 10);
  const label =
    sales.length === 1
      ? `Invoice_${sales[0].name}.pdf`
      : `Invoices_bulk_${sales.length}_${stamp}.pdf`;
  pdf.save(label);
}

export async function printInvoicePdf(sales: InvoicePdfSale[], opts: InvoicePdfOptions) {
  const pdf = await generateInvoicePdfDoc(sales, opts);

  try {
    const { usePrinterStore } = await import('@/store/printerStore');
    const { settings } = usePrinterStore.getState();
    if (settings.usePrintHelper && settings.windowsPrinterName) {
      const { printImageViaHelper } = await import('@/services/printHelper');
      if ((pdf as any).internal?.getNumberOfPages?.() > 1) {
        /* multi-page: fall through to browser PDF print */
      } else {
        const imgData = (pdf as any)._imgData || '';
        const imgBase64 = imgData.includes(',')
          ? imgData.split(',')[1]
          : pdf.output('datauristring').split(',')[1];
        await printImageViaHelper(settings.windowsPrinterName, imgBase64);
        const { default: toast } = await import('react-hot-toast');
        toast.success(`Invoice sent to ${settings.windowsPrinterName}`);
        return;
      }
    }
  } catch (helperErr) {
    console.warn('Silent Print Helper invoice print failed, falling back to browser tab', helperErr);
  }

  const blob = pdf.output('blob');
  const blobUrl = URL.createObjectURL(blob);
  const newWin = window.open(blobUrl, '_blank');
  if (newWin) {
    newWin.focus();
  } else {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.src = blobUrl;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    };
  }
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}
